import { Asset } from "../models/assets";
import { BulkAssetQueryRequest } from "../models/bulkActions";
import { PostBasedPaginatedResponse } from "../models/shared";
import {
  BatchPatchSystemRequest,
  BatchSystemsModifyAssignmentsPayload,
  System,
  SystemDTO,
  SystemQueryRequest,
  SystemQueryResponse,
} from "../models/systems";
import { ProjectId } from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";

/**
 * Default page size for `fetchAllAssetMemberships`'s pagination loop.
 * Mirrors the app's prior `SYSTEM_ASSET_MEMBERSHIPS_LIMIT` (systemsAPI.ts);
 * callers needing a different limit can pass one explicitly.
 */
const DEFAULT_ASSET_MEMBERSHIPS_LIMIT = 200;

export type SystemMembership = SystemDTO & { type: "system" };
export type AssetMembership = Asset & { type: "asset"; name: string };

/**
 * Pure fetch / request-body-building logic for the Systems domain.
 *
 * Extracted from `apps/acs-assets/src/clients/systemsAPI.ts` (#2504). That
 * file's 8 React-Query hooks stay in the app — this client is deliberately
 * React-free and dependency-light — and now call these plain async functions
 * internally instead of building `fetch` calls inline. Side effects that
 * depend on app-only concerns (cache invalidation, notifications, i18n,
 * exo-reporting) also stay in the app hooks.
 */
export const getSystemsClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => {
  const baseV1 = http.getAssetsServiceBasePath(projectId, "v1");
  const baseV2 = http.getAssetsServiceBasePath(projectId, "v2");

  const querySystemsAt = (
    url: string,
    request: SystemQueryRequest
  ): Promise<SystemQueryResponse> =>
    http.post<SystemQueryResponse>(url, { body: JSON.stringify(request) });

  const queryAssetsAt = (
    url: string,
    request: BulkAssetQueryRequest
  ): Promise<PostBasedPaginatedResponse<Asset, BulkAssetQueryRequest>> =>
    http.post<PostBasedPaginatedResponse<Asset, BulkAssetQueryRequest>>(url, {
      body: JSON.stringify(request),
    });

  /** POST `:query` on Systems — used directly by `useSystemsQueryAPI`, which
   * owns cursor/sort-mapping state and builds the request body itself. */
  const querySystems = (request: SystemQueryRequest): Promise<SystemQueryResponse> =>
    querySystemsAt(`${baseV1}/${Endpoints.Systems}:query`, request);

  /** POST `:query` on Systems, filtered by id — shared by `useGetSystemsByIds`
   * and `useGetAssetParentSystemsAPI`'s second step (resolve parent systems
   * by id once their ids are known). */
  const querySystemsByIds = (
    systemIds: System["id"][],
    limit?: number
  ): Promise<SystemQueryResponse> =>
    querySystems({
      query: {
        entityType: "systems_atomic_query_v1",
        includeFilters: { ids: systemIds },
        excludeFilters: {},
      },
      options: {
        includeDeleted: false,
        includedComputedFields: ["parentSystemIds"],
      },
      ...(limit ? { limit } : {}),
    });

  const batchCreateSystems = async (
    newSystem: Partial<System>
  ): Promise<System> =>
    (
      await http.post<{ results: System[] }>(
        `${baseV1}/${Endpoints.Systems}:batch-create`,
        { body: JSON.stringify([newSystem]) }
      )
    ).results[0];

  const batchDeleteSystems = (systemIds: System["id"][]): Promise<System> =>
    http.post<System>(`${baseV1}/${Endpoints.Systems}:batch-delete`, {
      body: JSON.stringify({ ids: systemIds }),
    });

  const batchPatchSystems = (patch: BatchPatchSystemRequest): Promise<System> =>
    http.patch<System>(`${baseV1}/${Endpoints.Systems}:batch-patch`, {
      body: JSON.stringify(patch),
    });

  const batchModifyAssignments = (
    payload: BatchSystemsModifyAssignmentsPayload[]
  ): Promise<System> =>
    http.post<System>(
      `${baseV1}/${Endpoints.Systems}:batch-modify-assignments`,
      { body: JSON.stringify({ data: payload }) }
    );

  /** Follows `pagination.nextPost` to collect every sub-system of `systemId`. */
  const fetchAllSystemMemberships = async (
    systemId: string
  ): Promise<SystemMembership[]> => {
    const toMembership = (system: SystemDTO): SystemMembership => ({
      ...system,
      type: "system",
    });

    let response = await querySystemsAt(`${baseV1}/${Endpoints.Systems}:query`, {
      query: {
        entityType: "systems_atomic_query_v1",
        includeFilters: { parentSystemIds: [systemId] },
        excludeFilters: {},
      },
      options: { includeDeleted: false },
    });

    const memberships: SystemMembership[] = [...response.results.map(toMembership)];
    let pagination = response.pagination;

    while (pagination.nextPost) {
      response = await querySystemsAt(
        pagination.nextPost.url,
        pagination.nextPost.body
      );
      pagination = response.pagination;
      memberships.push(...response.results.map(toMembership));
    }

    return memberships;
  };

  /** Follows `pagination.nextPost` to collect every asset member of `systemId`. */
  const fetchAllAssetMemberships = async (
    systemId: string,
    limit: number = DEFAULT_ASSET_MEMBERSHIPS_LIMIT
  ): Promise<AssetMembership[]> => {
    const toMembership = (asset: Asset): AssetMembership => ({
      ...asset,
      type: "asset",
      // Adds in the name due to an issue with sorting both systems and assets
      // with each having different properties for representing the entity
      // name in the memberships grid.
      name: asset.clientAssetId,
    });

    let response = await queryAssetsAt(`${baseV2}/${Endpoints.Assets}:query`, {
      query: {
        entityType: "assets_atomic_query_v2",
        includeFilters: { parentSystemIds: [systemId] },
        excludeFilters: {},
      },
      options: { includeDeleted: false },
      limit,
    } as BulkAssetQueryRequest);

    const memberships: AssetMembership[] = [...response.results.map(toMembership)];
    let pagination = response.pagination;

    while (pagination.nextPost) {
      response = await queryAssetsAt(
        pagination.nextPost.url,
        pagination.nextPost.body
      );
      pagination = response.pagination;
      memberships.push(...response.results.map(toMembership));
    }

    return memberships;
  };

  /**
   * Looks up a single asset's `computedFields.parentSystemIds` — used by
   * `useGetAssetParentSystemsAPI` to resolve an asset's parent system(s).
   * Named for the request's purpose (find this asset's parent system(s)),
   * not its filter shape — it queries Assets by `id`, not by `parentSystemIds`
   * (that's `fetchAllAssetMemberships`, the inverse lookup).
   */
  const queryAssetsByParentSystem = (
    assetId: string
  ): Promise<PostBasedPaginatedResponse<Asset, BulkAssetQueryRequest>> =>
    queryAssetsAt(`${baseV2}/${Endpoints.Assets}:query`, {
      query: {
        entityType: "assets_atomic_query_v2",
        includeFilters: { ids: [assetId] },
        excludeFilters: {},
      },
      options: {
        includeDeleted: false,
        includedComputedFields: ["parentSystemIds"],
      },
    } as BulkAssetQueryRequest);

  return {
    querySystems,
    querySystemsByIds,
    batchCreateSystems,
    batchDeleteSystems,
    batchPatchSystems,
    batchModifyAssignments,
    fetchAllSystemMemberships,
    fetchAllAssetMemberships,
    queryAssetsByParentSystem,
  };
};

/**
 * The shape returned by {@link getSystemsClient}. Exported so consumers can
 * type variables without spelling out `ReturnType<typeof getSystemsClient>`.
 */
export type SystemsClient = ReturnType<typeof getSystemsClient>;
